import {
  IconArrowRight,
  IconArrowUpRight,
  IconCertificate,
  IconClock,
  IconMail,
  IconMapPin,
  IconPhone,
} from "@tabler/icons-react";
import Image from "next/image";
import Link from "next/link";
import { COMPANY, ENGAGEMENTS_CTA, FOOTER_LINKS, UNEP_LOGO } from "@/lib/constants";
import { ObfuscatedEmailLink } from "./obfuscated-email";

export function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer className="relative overflow-hidden">
      <div className="relative px-6 md:px-10 lg:px-16 pt-16 md:pt-20">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 pb-14 md:pb-20 border-b border-border">
            <div className="max-w-xl">
              <p className="text-[11px] uppercase tracking-[0.2em] text-text-muted mb-4">
                Paysagiste en Côte d&apos;Émeraude
              </p>
              <Link
                href="/"
                aria-label="Symbiosis Paysage Services, accueil"
                className="inline-flex items-center"
              >
                <Image
                  src="/images/logo-symbiosis-full.webp"
                  alt="Symbiosis Paysage Services"
                  width={1360}
                  height={387}
                  className="h-14 w-auto md:h-16 lg:h-20"
                />
              </Link>
              <p className="mt-5 md:mt-6 text-(--text-primary)/75 leading-relaxed max-w-md">
                Création et entretien de jardins sur la Rance et la Côte d&apos;Émeraude.
              </p>
            </div>
            <div className="flex flex-col gap-3 self-start md:self-end">
              <div className="flex items-center gap-3 px-4 py-3 border border-(--primary-light)/30 bg-(--primary)/15 rounded-sm">
                <IconCertificate
                  size={20}
                  stroke={1.5}
                  className="text-primary-light"
                  aria-hidden
                />
                <span className="flex flex-col">
                  {/* Surtitre « Label » et non « Membre » : la valeur en
                      dessous commence déjà par « Membre UNEP », ce qui donnait
                      « MEMBRE / Membre UNEP depuis 2021 ». */}
                  <span className="text-[10px] uppercase tracking-[0.2em] text-(--text-primary)/80">
                    Label
                  </span>
                  <span className="text-sm text-text-primary">
                    {COMPANY.accreditation.label} depuis {COMPANY.accreditation.since}
                  </span>
                </span>
              </div>
              <div className="flex items-center gap-3 px-4 py-3 border border-(--primary-light)/30 bg-(--primary)/15 rounded-sm">
                <a
                  href="https://www.servicesalapersonne.gouv.fr"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Services à la Personne (site officiel)"
                  className="shrink-0"
                >
                  <Image
                    src="/SAP-symbiosis.png"
                    alt="Logo officiel Services à la Personne"
                    width={72}
                    height={60}
                    className="h-auto w-16"
                  />
                </a>
                <span className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-(--text-primary)/80">
                    Agrément
                  </span>
                  <span className="text-sm text-text-primary">
                    Services à la Personne ({COMPANY.sap.short})
                  </span>
                </span>
              </div>
            </div>
          </div>

          <div className="grid gap-12 md:gap-16 md:grid-cols-2 py-14 md:py-20">
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-text-muted mb-5">Contact</p>
              <ul className="space-y-4 text-sm">
                <li>
                  <a
                    href={`tel:${COMPANY.phoneHref}`}
                    className="group inline-flex items-center gap-3 text-base md:text-lg text-text-primary hover:text-primary-light transition-colors tabular-nums"
                  >
                    <IconPhone
                      size={15}
                      stroke={1.75}
                      className="text-text-muted group-hover:text-primary-light transition-colors shrink-0"
                      aria-hidden
                    />
                    <span>{COMPANY.phoneDisplay}</span>
                  </a>
                </li>
                <li className="group flex items-center gap-3 text-(--text-primary)/85">
                  <IconMail
                    size={15}
                    stroke={1.75}
                    className="text-text-muted group-hover:text-primary-light transition-colors shrink-0"
                    aria-hidden
                  />
                  <ObfuscatedEmailLink
                    email={COMPANY.email}
                    className="hover:text-primary-light transition-colors"
                  />
                </li>
                <li className="flex items-start gap-3 text-(--text-primary)/75 leading-relaxed pt-1">
                  <IconMapPin
                    size={15}
                    stroke={1.75}
                    className="text-text-muted mt-1 shrink-0"
                    aria-hidden
                  />
                  <span>
                    {COMPANY.address.street}
                    <br />
                    {COMPANY.address.postalCode} {COMPANY.address.city}
                  </span>
                </li>
                <li className="flex items-center gap-3 pt-2 text-xs text-text-muted">
                  <IconClock
                    size={14}
                    stroke={1.75}
                    className="text-primary-light shrink-0"
                    aria-hidden
                  />
                  <span>{COMPANY.hoursDisplay}</span>
                </li>
              </ul>
            </div>

            <div className="md:max-w-xs md:ml-auto md:text-right">
              <p className="text-[11px] uppercase tracking-[0.2em] text-text-muted mb-5">
                Navigation
              </p>
              <ul className="space-y-3 text-sm">
                {FOOTER_LINKS.map((link) => (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      className="inline-flex items-center gap-1.5 text-(--text-primary)/85 hover:text-primary-light transition-colors group"
                    >
                      <span>{link.label}</span>
                      <span className="h-px w-0 group-hover:w-4 bg-primary-light transition-all duration-300" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="pt-8 pb-8 border-t border-border flex flex-col md:flex-row md:items-center md:justify-between gap-5 md:gap-8">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
              <Image
                src={UNEP_LOGO}
                alt="Logo UNEP - Union Nationale des Entreprises du Paysage"
                width={160}
                height={71}
                className="h-14 md:h-16 w-auto shrink-0"
              />
              <div className="max-w-md">
                {/* « chaque jardin compte » figure déjà dans le logo UNEP à gauche. */}
                <p className="text-sm text-(--text-primary)/85 leading-relaxed">
                  Membre de l&apos;Union Nationale des Entreprises du Paysage, notre entreprise
                  s&apos;engage.
                </p>
              </div>
            </div>
            <Link
              href={ENGAGEMENTS_CTA.href}
              className="group inline-flex items-center gap-1.5 text-sm text-text-primary hover:text-primary-light transition-colors self-start md:self-auto"
            >
              <span>{ENGAGEMENTS_CTA.label}</span>
              <IconArrowRight
                size={14}
                stroke={1.75}
                className="group-hover:translate-x-0.5 transition-transform"
                aria-hidden
              />
            </Link>
          </div>

          <div className="pt-8 pb-10 border-t border-border flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-text-muted">
            <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-5 flex-wrap">
              <p>
                &copy; {year} {COMPANY.name}. Tous droits réservés.
              </p>
              <span aria-hidden className="hidden md:inline h-3 w-px bg-border" />
              <Link href="/mentions-legales" className="hover:text-text-primary transition-colors">
                Mentions légales
              </Link>
              <span aria-hidden className="hidden md:inline h-3 w-px bg-border" />
              <Link
                href="/politique-de-confidentialite"
                className="hover:text-text-primary transition-colors"
              >
                Politique de confidentialité
              </Link>
            </div>
            <a
              href="https://djannistudio.fr"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 text-text-muted hover:text-text-primary transition-colors"
            >
              <span>Site réalisé par</span>
              <span className="text-(--text-primary)/90 group-hover:text-primary-light transition-colors">
                Djanni Studio
              </span>
              <IconArrowUpRight
                size={14}
                stroke={1.75}
                className="text-text-muted group-hover:text-primary-light group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
                aria-hidden
              />
            </a>
          </div>
        </div>
      </div>

      <div
        aria-hidden
        className="hidden md:block relative -mb-6 md:-mb-8 overflow-hidden pointer-events-none select-none"
      >
        <div
          aria-hidden="true"
          role="presentation"
          className="text-center leading-none whitespace-nowrap text-transparent"
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(5rem, 22vw, 18rem)",
            backgroundImage: "linear-gradient(to bottom, rgba(45,95,63,0.14), rgba(45,95,63,0.05))",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
          }}
        >
          Symbiosis
        </div>
      </div>
    </footer>
  );
}
